/**
 * Owner: Masa Bayu
 * File: BannerCarousel.tsx
 * Date:Time: 2026-10-17 14:30
 * Description: Karusel banner hero — 7 slaid qx1–qx7 (2048×768, nisbah 8:3).
 *   Auto-putar · jeda hover/fokus · butang prev/next · titik navigasi.
 *   Label mengikut locale. §410 — gaya disamakan hero home (marketplace-banner.css).
 */

import { useEffect, useState, type ReactElement } from 'react';
import { QxIcon } from '../../ui01';
import { HERO_BANNER_SLIDES } from '../../ux/hero-banners';
import type { MarketplaceLabels } from './marketplace-locale';

const AUTO_MS = 5500;

export interface BannerCarouselProps {
  readonly t: MarketplaceLabels;
}

export function BannerCarousel({ t }: BannerCarouselProps): ReactElement {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const slideCount = HERO_BANNER_SLIDES.length;
  const activeAlt = `${t.banner.altPrefix} ${String(index + 1)}`;

  const go = (next: number) => {
    setIndex(((next % slideCount) + slideCount) % slideCount);
  };

  useEffect(() => {
    if (paused || slideCount < 2) {
      return;
    }
    if (typeof window.matchMedia === 'function') {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (reduce.matches) {
        return;
      }
    }
    const id = window.setInterval(() => {
      setIndex((current) => (current + 1) % slideCount);
    }, AUTO_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [paused, slideCount]);

  return (
    <div
      className="hmk__banner"
      role="region"
      aria-roledescription="carousel"
      aria-label={t.banner.carouselLabel}
      onMouseEnter={() => {
        setPaused(true);
      }}
      onMouseLeave={() => {
        setPaused(false);
      }}
      onFocusCapture={() => {
        setPaused(true);
      }}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setPaused(false);
        }
      }}
    >
      <div className="hmk__banner-slides">
        {HERO_BANNER_SLIDES.map((slide, slideIndex) => (
          <div
            key={slide.id}
            className={slideIndex === index ? 'hmk__banner-slide is-active' : 'hmk__banner-slide'}
            aria-hidden={slideIndex === index ? undefined : true}
          >
            <img
              className="hmk__banner-image"
              src={slide.src}
              alt={slideIndex === index ? `${t.banner.altPrefix} ${String(slideIndex + 1)}` : ''}
              draggable={false}
            />
          </div>
        ))}
      </div>

      <div className="hmk__banner-controls">
        <button
          type="button"
          className="hmk__banner-nav"
          aria-label={t.banner.previous}
          onClick={() => {
            go(index - 1);
          }}
        >
          <QxIcon name="page-prev" size="md" />
        </button>
        <button
          type="button"
          className="hmk__banner-nav"
          aria-label={t.banner.next}
          onClick={() => {
            go(index + 1);
          }}
        >
          <QxIcon name="page-next" size="md" />
        </button>
      </div>

      <div className="hmk__banner-dots" role="tablist" aria-label={t.banner.slidesLabel}>
        {HERO_BANNER_SLIDES.map((slide, slideIndex) => (
          <button
            key={slide.id}
            type="button"
            role="tab"
            aria-selected={slideIndex === index}
            aria-label={`${t.banner.altPrefix} ${String(slideIndex + 1)}`}
            className={slideIndex === index ? 'hmk__banner-dot is-active' : 'hmk__banner-dot'}
            onClick={() => {
              setIndex(slideIndex);
            }}
          />
        ))}
      </div>

      <span className="visually-hidden" aria-live="polite">
        {activeAlt} · {index + 1} / {slideCount}
      </span>
    </div>
  );
}
